import { useState, useMemo } from "react";
import { Line, Bar } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
  Filler,
} from "chart.js";
import { Box, Group, Select, Text, Loader, Paper, Stack } from "@mantine/core";
import { DatePickerInput } from "@mantine/dates";
import { useGetParentsLoginStatsQuery } from "../redux/features/parentApi";

// Enregistrer les composants Chart.js
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
  Filler
);

/**
 * Composant de graphique pour les statistiques de connexion des parents
 */
const LoginStatsChart = ({ title = "Activité de connexion des parents" }) => {
  // État pour les filtres
  const [dateRange, setDateRange] = useState([
    new Date(new Date().setDate(new Date().getDate() - 30)),
    new Date(),
  ]);
  const [groupBy, setGroupBy] = useState("day");
  const [chartType, setChartType] = useState("line");

  // Formater les dates pour l'API
  const formatDate = (date) => {
    if (!date) return null;
    return date.toISOString().split("T")[0];
  };

  // Requête API
  const { data, isLoading, error } = useGetParentsLoginStatsQuery(
    {
      startDate: formatDate(dateRange[0]),
      endDate: formatDate(dateRange[1]),
      groupBy,
    },
    {
      skip: !dateRange[0] || !dateRange[1],
    }
  );

  // Préparer les données du graphique
  const chartData = useMemo(() => {
    if (!data?.data?.stats) return null;

    const stats = data.data.stats;
    const labels = stats.map((s) => {
      const date = new Date(s.date);
      if (groupBy === "month") {
        return date.toLocaleDateString("fr-FR", { month: "short", year: "numeric" });
      } else if (groupBy === "week") {
        return `Sem. ${date.toLocaleDateString("fr-FR", { day: "2-digit", month: "short" })}`;
      }
      return date.toLocaleDateString("fr-FR", { day: "2-digit", month: "short" });
    });

    return {
      labels,
      datasets: [
        {
          label: "Connexions",
          data: stats.map((s) => s.count),
          borderColor: "rgb(59, 130, 246)",
          backgroundColor: chartType === "line" 
            ? "rgba(59, 130, 246, 0.1)" 
            : "rgba(59, 130, 246, 0.7)",
          fill: chartType === "line",
          tension: 0.4,
        },
      ],
    };
  }, [data, groupBy, chartType]);

  // Options du graphique
  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: false,
      },
      tooltip: {
        mode: "index",
        intersect: false,
      },
    },
    scales: {
      y: {
        beginAtZero: true,
        ticks: {
          stepSize: 1,
        },
      },
    },
  };

  return (
    <Paper shadow="sm" p="md" radius="md" withBorder>
      <Stack spacing="md">
        {/* En-tête */}
        <Group position="apart">
          <Text weight={600} size="lg">
            {title}
          </Text>
          {data?.data && (
            <Group spacing="xl">
              <Box>
                <Text size="xs" color="dimmed">Total connexions</Text>
                <Text weight={700} size="xl" color="blue">
                  {data.data.total_connections}
                </Text>
              </Box>
              <Box>
                <Text size="xs" color="dimmed">Parents uniques</Text>
                <Text weight={700} size="xl" color="green">
                  {data.data.unique_parents}
                </Text>
              </Box>
            </Group>
          )}
        </Group>

        {/* Filtres */}
        <Group>
          <DatePickerInput
            type="range"
            label="Période"
            placeholder="Sélectionner une période"
            value={dateRange}
            onChange={setDateRange}
            clearable={false}
            style={{ flex: 1, maxWidth: 300 }}
          />
          <Select
            label="Grouper par"
            value={groupBy}
            onChange={setGroupBy}
            data={[
              { value: "day", label: "Jour" },
              { value: "week", label: "Semaine" },
              { value: "month", label: "Mois" },
            ]}
            style={{ width: 130 }}
          />
          <Select
            label="Type de graphique"
            value={chartType}
            onChange={setChartType}
            data={[
              { value: "line", label: "Ligne" },
              { value: "bar", label: "Barres" },
            ]}
            style={{ maxWidth: 170 }}
          />
        </Group>

        {/* Graphique */}
        <Box style={{ height: 300 }}>
          {isLoading ? (
            <Group position="center" style={{ height: "100%" }}>
              <Loader />
            </Group>
          ) : error ? (
            <Group position="center" style={{ height: "100%" }}>
              <Text color="red">Erreur lors du chargement des données</Text>
            </Group>
          ) : chartData ? (
            chartType === "line" ? (
              <Line data={chartData} options={chartOptions} />
            ) : (
              <Bar data={chartData} options={chartOptions} />
            )
          ) : (
            <Group position="center" style={{ height: "100%" }}>
              <Text color="dimmed">Sélectionnez une période pour voir les statistiques</Text>
            </Group>
          )}
        </Box>
      </Stack>
    </Paper>
  );
};

export default LoginStatsChart;
